import * as React from 'react';
import * as MuiCore from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AppRoute } from '../../routes';
import { Icon } from '../../lib/icons';
import { Theme } from '@mui/material/styles';

type Props<TValue, TOptions> = {
    group: {
        name?: string;
        items: TValue[];
        headerComponent?: React.ReactElement;
        footerComponent?: React.ReactElement;
    };
    component: React.FunctionComponent<{
        testId?: string;
        value: TValue;
        appRoute?: AppRoute;
        isLast: boolean;
        options?: TOptions;
        onClick?: () => void;
    }>;
    componentTestId?: string;
    appRoute?: AppRoute;
    options?: TOptions;
    style?: any;
    navigationFn?: (arg: TValue) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
    expand: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
}));

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function makeListGroupComponent<TValue, TOptions = never>() {
    const component: React.FC<Props<TValue, TOptions>> = (props) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classes = useStyles();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [open, setOpen] = React.useState(true);

        const ListItemComponent = props.component;
      
        return (
            <div data-testid="list-group">
                <MuiCore.Card style={{ textDecorationSkipInk: 'inherit', ...props.style }}>
                    <MuiCore.List
                        component="div"
                        disablePadding
                        subheader={
                            props.group.name ? (
                                <MuiCore.ListSubheader
                                    onClick={(): void => {
                                        setOpen(!open);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <MuiCore.ListItemText
                                        data-testid="list-group-title"
                                        primary={props.group.name}
                                        primaryTypographyProps={{
                                            variant: 'subtitle2',
                                            color: 'primary',
                                            style: { fontWeight: 600, lineHeight: 3 },
                                        }}
                                    />
                                    
                                            <MuiCore.ListItemSecondaryAction>
                                                <MuiCore.IconButton>
                                                    <Icon.ExpandMore
                                                        className={`${classes.expand} ${
                                                            open ? classes.expandOpen : ''
                                                        }`}
                                                    />
                                                </MuiCore.IconButton>
                                            </MuiCore.ListItemSecondaryAction>
                                        </MuiCore.ListSubheader>
                                    ) : (
                                        undefined
                                    )
                                }
                            >
                                <MuiCore.Divider />
                                {props.group.headerComponent}
                                <MuiCore.Collapse in={open} timeout="auto" unmountOnExit>
                                    <MuiCore.List component="div" disablePadding>
                                        {props.group.items.map((item: any, listItemIndex) => (   
                                              <div key={listItemIndex}> 
                                                <ListItemComponent
                                                    testId={props.componentTestId}                                                    
                                                    value={item}
                                                    appRoute={props.appRoute}
                                                    isLast={listItemIndex + 1 === props.group.items.length}
                                                    options={props.options}
                                                    onClick={(): void => props.navigationFn && props.navigationFn(item)}
                                                ></ListItemComponent>
                                            </div>
                                        ))}
                                        {props.group.footerComponent}
                                    </MuiCore.List>
                                </MuiCore.Collapse>
                            </MuiCore.List>                   
                </MuiCore.Card>
            </div>
        );
    };

    return component;
}
